import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  createClass, 
  getFacilityClasses, 
  updateClass, 
  deleteClass,
  getClassRegistrations,
  getFacilityBusinessHours,
  staffApi,
  businessUserApi,
  courtApi,
  memberApi
} from '../api';
import { reservationApi } from '../reservation_api';
import { Class, ClassRegistration } from '../api_types';
import { format, parseISO, startOfDay } from 'date-fns';
import { logger } from '../shared/logger';
import { Box, Chip } from '@mui/material';
import LoadingSpinner from '../shared/components/LoadingSpinner';

interface ClassManagementProps {
  facilityId: string;
  businessId: string;
}


interface BusinessHours {
  [day: string]: {
    isClosed: boolean;
    openTime: string;
    closeTime: string;
  };
}

interface UserOption {
  id: string;
  name: string;
  type: 'staff' | 'business';
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface Court {
  id: string;
  name: string;
  type: string;
}

const ClassManagement: React.FC<ClassManagementProps> = ({ facilityId, businessId }) => {
  const { user } = useAuth();
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [registrations, setRegistrations] = useState<Record<string, ClassRegistration[]>>({});
  const [businessHours, setBusinessHours] = useState<BusinessHours>({});
  const [timeOptions, setTimeOptions] = useState<string[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [members, setMembers] = useState<Record<string, Member>>({});
  const [conflictError, setConflictError] = useState<string | null>(null);
  const [courts, setCourts] = useState<Court[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructorId: '',
    courtIds: [] as string[],
    maxCapacity: 10,
    price: 0,
    startTime: '',
    endTime: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    status: 'scheduled' as 'scheduled' | 'cancelled' | 'completed'
  });

  useEffect(() => {
    loadClasses();
    loadBusinessHours();
    loadUsers();
    loadCourts();
  }, [facilityId]);

  useEffect(() => {
    if (Object.keys(businessHours).length > 0) {
      generateTimeOptions(formData.date);
    }
  }, [formData.date, businessHours]);

  // Add real-time conflict checking effect
  useEffect(() => {
    let isMounted = true;
    async function checkConflict() {
      if (formData.courtIds.length > 0 && formData.startTime && formData.endTime) {
        let foundConflict: string | null = null;
        for (const courtId of formData.courtIds) {
          const result = await reservationApi.checkTimeConflicts(
            businessId,
            facilityId,
            {
              courtId,
              date: formData.date,
              startTime: formData.startTime,
              endTime: formData.endTime,
              excludeEventId: selectedClass?.id,
              checkOneTimeEvents: true,
              checkRecurringEvents: true,
              checkReservations: true,
              checkClasses: true
            }
          );
          if (result.hasConflict) {
            foundConflict = result.conflictMessage || 'There is a scheduling conflict.';
            break;
          }
        }
        if (isMounted) {
          setConflictError(foundConflict);
        }
      } else {
        if (isMounted) {
          setConflictError(null);
        }
      }
    }
    checkConflict();
    return () => { isMounted = false; };
  }, [formData.courtIds, formData.startTime, formData.endTime, formData.date, selectedClass, facilityId, businessId]);

  const loadBusinessHours = async () => {
    try {
      const hours = await getFacilityBusinessHours(businessId, facilityId);
      setBusinessHours(hours);
      // Generate time options for today's date
      generateTimeOptions(format(new Date(), 'yyyy-MM-dd'));
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load business hours');
      logger.error('Error loading business hours:', error);
    }
  };

  const generateTimeOptions = (date: string) => {
    const parsedDate = parseISO(date);
    const dayOfWeek = format(parsedDate, 'EEEE').toLowerCase();
    const hours = businessHours[dayOfWeek];
    
    if (!hours || hours.isClosed) {
      setTimeOptions([]);
      return;
    }

    const options = [];
    const [openHour, openMinute] = hours.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = hours.closeTime.split(':').map(Number);

    const openTimeMinutes = openHour * 60 + openMinute;
    const closeTimeMinutes = closeHour * 60 + closeMinute;

    for (let minutes = openTimeMinutes; minutes < closeTimeMinutes; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push(time);
    }
    setTimeOptions(options);
  };

  const formatTimeToAMPM = (time: string) => {
    if (!time) return 'Not set';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${period}`;
  };

  const calculateEndTime = (startTime: string, duration: '1hour' | '1.5hour' | '2hour') => {
    const [hours, minutes] = startTime.split(':').map(Number);
    let durationInMinutes = 60; // Default to 1 hour
    if (duration === '1.5hour') {
      durationInMinutes = 90;
    } else if (duration === '2hour') {
      durationInMinutes = 120;
    }
    
    const endDate = new Date();
    endDate.setHours(hours, minutes + durationInMinutes);
    return `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
  };

  const handleTimeSlotSelect = (time: string) => {
    const endTime = calculateEndTime(time, '1hour');
    setFormData({
      ...formData,
      startTime: time,
      endTime: endTime
    });
  };

  const handleManualTimeChange = (time: string, type: 'start' | 'end') => {
    if (type === 'start') {
      setFormData({
        ...formData,
        startTime: time,
        endTime: calculateEndTime(time, '1hour')
      });
    } else {
      setFormData({
        ...formData,
        endTime: time
      });
    }
  };

  const loadMembers = async (memberIds: string[]) => {
    if (memberIds.length === 0) return;
  
    const membersData: Record<string, Member> = {};
  
    for (const memberId of memberIds) {
      try {
        const memberDetails = await memberApi.getMemberDetails(businessId, memberId, facilityId);
        membersData[memberId] = {
          id: memberId,
          firstName: memberDetails.firstName,
          lastName: memberDetails.lastName,
          email: '' // Email is not returned by the API
        };
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load member details');
        logger.error(`Error loading member details for ${memberId}:`, error);
      }
    }
  
    setMembers(membersData);
  };

  const loadClasses = async () => {
    try {
      setLoading(true);
      const facilityClasses = await getFacilityClasses(businessId, facilityId);
      setClasses(facilityClasses);
      
      // Load registrations for each class
      const registrationsMap: Record<string, ClassRegistration[]> = {};
      for (const classItem of facilityClasses) {
        const classRegistrations = await getClassRegistrations(businessId, facilityId, classItem.id);
        registrationsMap[classItem.id] = classRegistrations;
      }
      setRegistrations(registrationsMap);

      // Load member information for all registered members
      const allMemberIds = facilityClasses.flatMap((classItem: Class) => classItem.registeredMembers);
      loadMembers(allMemberIds);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load classes');
      logger.error('Error loading classes:', error);
      setError('Failed to load classes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const [staffUsers, businessUsers] = await Promise.all([
        staffApi.getStaffUsers(businessId),
        businessUserApi.getBusinessUsers(businessId)
      ]);

      const staffOptions = staffUsers
        .filter(staff => staff.facility_id === facilityId)
        .map(staff => ({
          id: staff.id,
          name: `${staff.name} (Staff)`,
          type: 'staff' as const
        }));

      const businessOptions = businessUsers.map(business => ({
        id: business.id,
        name: `${business.name} (Business)`,
        type: 'business' as const
      }));

      setUsers([...staffOptions, ...businessOptions]);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load users');
      logger.error('Error loading users:', error);
    }
  };

  const loadCourts = async () => {
    try {
      const facilityCourts = await courtApi.getCourts(businessId, facilityId);
      setCourts(facilityCourts);
    } catch (err) {
      console.error('Error loading courts:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setConflictError(null);

      // Check for conflicts using the new API endpoint
      let foundConflict: string | null = null;
      for (const courtId of formData.courtIds) {
        const result = await reservationApi.checkTimeConflicts(
          businessId,
          facilityId,
          {
            courtId,
            date: formData.date,
            startTime: formData.startTime,
            endTime: formData.endTime,
            excludeEventId: selectedClass?.id,
            checkOneTimeEvents: true,
            checkRecurringEvents: true,
            checkReservations: true,
            checkClasses: true
          }
        );
        if (result.hasConflict) {
          foundConflict = result.conflictMessage || 'There is a scheduling conflict.';
          break;
        }
      }

      if (foundConflict) {
        setConflictError(foundConflict);
        return;
      }

      // Ensure the date is saved in the correct format without timezone issues
      const selectedDate = startOfDay(parseISO(formData.date));
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');

      const classData = {
        ...formData,
        businessId: businessId!,
        facilityId: facilityId!,
        date: formattedDate,
        status: 'scheduled' as const
      };

      if (selectedClass) {
        await updateClass(businessId, facilityId, selectedClass.id, classData);
        setSuccessMessage('Class updated successfully!');
      } else {
        await createClass(businessId, facilityId, classData);
        setSuccessMessage('Class created successfully!');
      }
      setShowModal(false);
      loadClasses();
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setError('Failed to save class');
      console.error(err);
    }
  };

  const handleDelete = async (classId: string) => {
    if (window.confirm('Are you sure you want to delete this class?')) {
      try {
        await deleteClass(businessId, facilityId, classId);
        loadClasses();
      } catch (err) {
        setError('Failed to delete class');
        console.error(err);
      }
    }
  };

  const handleEdit = (classItem: Class) => {
    setSelectedClass(classItem);
    // Ensure the date is displayed correctly in the form
    const parsedDate = parseISO(classItem.date);
    const formattedDate = format(parsedDate, 'yyyy-MM-dd');
    
    setFormData({
      title: classItem.title,
      description: classItem.description,
      instructorId: classItem.instructorId,
      courtIds: classItem.courtIds,
      maxCapacity: classItem.maxCapacity,
      price: classItem.price,
      startTime: classItem.startTime,
      endTime: classItem.endTime,
      date: formattedDate,
      status: classItem.status
    });
    generateTimeOptions(formattedDate);
    setShowModal(true);
  };

  const handleCreateNew = () => {
    setSelectedClass(null);
    const today = startOfDay(new Date());
    const formattedDate = format(today, 'yyyy-MM-dd');
    
    setFormData({
      title: '',
      description: '',
      instructorId: '',
      courtIds: [],
      maxCapacity: 10,
      price: 0,
      startTime: '',
      endTime: '',
      date: formattedDate,
      status: 'scheduled'
    });
    generateTimeOptions(formattedDate);
    setShowModal(true);
  };

  // Add new function to categorize lessons
  const categorizeLessons = (lessons: Class[]) => {
    const now = new Date();
    return lessons.reduce((acc, lesson) => {
      const lessonDate = parseISO(lesson.date);
      const lessonTime = lesson.startTime.split(':').map(Number);
      lessonDate.setHours(lessonTime[0], lessonTime[1]);
      
      if (lessonDate < now) {
        acc.past.push(lesson);
      } else {
        acc.upcoming.push(lesson);
      }
      return acc;
    }, { past: [] as Class[], upcoming: [] as Class[] });
  };

  // Add helper function to get status display
  const getStatusDisplay = (classItem: Class, isPast: boolean) => {
    if (isPast) return null; // Return null for past classes
    if (classItem.status === 'cancelled') return 'Cancelled';
    if (classItem.status === 'completed') return 'Completed';
    return 'Scheduled';
  };

  // Add helper function to get status styles
  const getStatusStyles = (classItem: Class, isPast: boolean) => {
    if (isPast) return ''; // Return empty string for past classes
    if (classItem.status === 'cancelled') return 'bg-red-50 text-red-600';
    if (classItem.status === 'completed') return 'bg-green-50 text-green-700';
    return 'bg-indigo-50 text-indigo-700';
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-[#f6f8fa]">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm border-b flex items-center justify-between px-8 py-5 mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Lessons and Classes</h1>
        <button
          onClick={handleCreateNew}
          className="inline-flex items-center px-5 py-2.5 bg-indigo-600 text-white font-semibold rounded-md shadow-sm hover:bg-indigo-700 transition"
        >
          Create New Class
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-center font-semibold shadow-sm">
            {successMessage}
          </div>
        )}

        {classes.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-12 text-center text-gray-500 text-lg mt-16">
            No classes have been created yet.
          </div>
        ) : (
          <div className="space-y-12">
            {/* Upcoming Classes Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Upcoming Classes</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {categorizeLessons(classes).upcoming.map((classItem) => (
                  <div
                    key={classItem.id}
                    className="relative bg-white rounded-xl shadow group hover:shadow-lg transition border-l-4 border-indigo-500/80 p-6 flex flex-col min-h-[260px]"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-lg font-semibold text-gray-900 truncate pr-2">{classItem.title}</h2>
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getStatusStyles(classItem, false)}`}
                      >
                        {getStatusDisplay(classItem, false)}
                      </span>
                    </div>
                    <div className="text-gray-600 text-sm mb-4 line-clamp-2 min-h-[40px]">{classItem.description}</div>
                    <div className="flex flex-col gap-1 text-sm text-gray-700 mb-4">
                      <div>
                        <span className="font-medium">Date:</span> {format(parseISO(classItem.date), 'MMM d, yyyy')}
                      </div>
                      <div>
                        <span className="font-medium">Time:</span> {classItem.startTime} - {classItem.endTime}
                      </div>
                      <div>
                        <span className="font-medium">Price:</span> ${classItem.price}
                      </div>
                      <div>
                        <span className="font-medium">Capacity:</span> {classItem.registeredMembers.length}/{classItem.maxCapacity}
                      </div>
                    </div>
                    {classItem.registeredMembers.length > 0 && (
                      <div className="mb-3">
                        <h3 className="font-medium text-xs text-gray-500 mb-1">Enrolled Members:</h3>
                        <ul className="space-y-0.5">
                          {classItem.registeredMembers.map((memberId) => {
                            const member = members[memberId];
                            return (
                              <li key={memberId} className="text-xs text-gray-700">
                                {member ? `${member.firstName} ${member.lastName}` : 'Loading...'}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    <div className="mt-auto flex space-x-2 pt-2">
                      <button
                        onClick={() => handleEdit(classItem)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md font-medium hover:bg-indigo-700 transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(classItem.id)}
                        className="px-4 py-2 bg-red-500 text-white rounded-md font-medium hover:bg-red-600 transition"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Past Classes Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Past Classes</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {categorizeLessons(classes).past.map((classItem) => (
                  <div
                    key={classItem.id}
                    className="relative bg-white rounded-xl shadow group hover:shadow-lg transition border-l-4 border-gray-300 p-6 flex flex-col min-h-[260px]"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-lg font-semibold text-gray-900 truncate pr-2">{classItem.title}</h2>
                      {getStatusDisplay(classItem, true) && (
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getStatusStyles(classItem, true)}`}
                        >
                          {getStatusDisplay(classItem, true)}
                        </span>
                      )}
                    </div>
                    <div className="text-gray-600 text-sm mb-4 line-clamp-2 min-h-[40px]">{classItem.description}</div>
                    <div className="flex flex-col gap-1 text-sm text-gray-700 mb-4">
                      <div>
                        <span className="font-medium">Date:</span> {format(parseISO(classItem.date), 'MMM d, yyyy')}
                      </div>
                      <div>
                        <span className="font-medium">Time:</span> {classItem.startTime} - {classItem.endTime}
                      </div>
                      <div>
                        <span className="font-medium">Price:</span> ${classItem.price}
                      </div>
                      <div>
                        <span className="font-medium">Capacity:</span> {classItem.registeredMembers.length}/{classItem.maxCapacity}
                      </div>
                    </div>
                    {classItem.registeredMembers.length > 0 && (
                      <div className="mb-3">
                        <h3 className="font-medium text-xs text-gray-500 mb-1">Enrolled Members:</h3>
                        <ul className="space-y-0.5">
                          {classItem.registeredMembers.map((memberId) => {
                            const member = members[memberId];
                            return (
                              <li key={memberId} className="text-xs text-gray-700">
                                {member ? `${member.firstName} ${member.lastName}` : 'Loading...'}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    <div className="mt-auto flex space-x-2 pt-2">
                      <button
                        onClick={() => handleEdit(classItem)}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md font-medium hover:bg-gray-700 transition"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {selectedClass ? 'Edit Class' : 'Create New Class'}
            </h2>
            {conflictError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-center font-semibold">
                {conflictError}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Instructor</label>
                  <select
                    value={formData.instructorId}
                    onChange={(e) => setFormData({ ...formData, instructorId: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select an instructor</option>
                    {users.map(user => (
                      <option key={`${user.type}-${user.id}`} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => {
                      const selectedDate = startOfDay(parseISO(e.target.value));
                      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
                      setFormData({ ...formData, date: formattedDate });
                      generateTimeOptions(formattedDate);
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Courts</label>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    {courts
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((court) => (
                        <Chip
                          key={court.id}
                          label={court.name}
                          onClick={() => {
                            const newCourtIds = formData.courtIds.includes(court.id)
                              ? formData.courtIds.filter(id => id !== court.id)
                              : [...formData.courtIds, court.id];
                            setFormData({ ...formData, courtIds: newCourtIds });
                          }}
                          sx={{
                            backgroundColor: formData.courtIds.includes(court.id)
                              ? 'rgba(98, 90, 250, 0.1)'
                              : 'transparent',
                            color: formData.courtIds.includes(court.id)
                              ? '#625afa'
                              : '#4f566b',
                            border: formData.courtIds.includes(court.id)
                              ? '1px solid #625afa'
                              : '1px solid rgba(0, 0, 0, 0.1)',
                            borderRadius: '6px',
                            height: '24px',
                            fontSize: '12px',
                            '&:hover': {
                              backgroundColor: 'rgba(98, 90, 250, 0.04)',
                              color: '#625afa',
                            },
                          }}
                        />
                      ))}
                  </Box>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Time</label>
                    <select
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formData.startTime}
                      onChange={(e) => handleManualTimeChange(e.target.value, 'start')}
                      required
                    >
                      <option value="">Select start time</option>
                      {timeOptions.map((time) => (
                        <option key={`start-${time}`} value={time}>
                          {formatTimeToAMPM(time)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Time</label>
                    <select
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formData.endTime}
                      onChange={(e) => handleManualTimeChange(e.target.value, 'end')}
                      required
                    >
                      <option value="">Select end time</option>
                      {timeOptions.map((time) => (
                        <option key={`end-${time}`} value={time}>
                          {formatTimeToAMPM(time)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Price</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Max Capacity</label>
                  <input
                    type="number"
                    value={formData.maxCapacity}
                    onChange={(e) => setFormData({ ...formData, maxCapacity: parseInt(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!!conflictError}
                >
                  {selectedClass ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassManagement; 